import { ApiError, describeError } from '../../services/errors';

/**
 * Sobreposição de mensagens no contexto de exportação (SPEC-023 §12).
 *
 * Dois códigos do catálogo são reusados pelo export com significado diferente
 * do da pesquisa, e o texto global está correto lá:
 *
 * - `SEARCH_LIMIT_REACHED` na pesquisa é o limite mensal de coleta; no export é
 *   o recorte passar do teto de linhas.
 * - `UNSUPPORTED_OPERATION` na pesquisa é a plataforma; no export é o formato.
 *
 * Trocar o texto global degradaria a mensagem da pesquisa. A escolha continua
 * sendo pelo `code`, nunca pela `message` do servidor (§50) — apenas
 * reconhecendo que o mesmo código pode ter leitura diferente em telas
 * diferentes.
 */
const EXPORT_MESSAGES: Record<string, string> = {
  SEARCH_LIMIT_REACHED:
    'O recorte selecionado tem linhas demais. Refine os filtros e tente de novo.',
  UNSUPPORTED_OPERATION: 'Este formato de exportação ainda não está disponível.',
};

export function describeExportError(error: unknown): string {
  if (!ApiError.is(error)) return describeError(error);

  const override = EXPORT_MESSAGES[error.code];
  if (!override) return describeError(error);

  // O backend informa o teto; quando informa, dizer o número é mais útil do que
  // pedir para "refinar os filtros" sem alvo.
  if (error.code === 'SEARCH_LIMIT_REACHED') {
    const maxRows = error.details?.maxRows;
    if (typeof maxRows === 'number') {
      return `O recorte selecionado passa de ${formatInteger(maxRows)} linhas. Refine os filtros e tente de novo.`;
    }
  }

  return override;
}

export function formatInteger(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value);
}

/** Tamanho de arquivo legível: o usuário decide se espera pelo download. */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}
